/**
 * Vault Hire — Admin AI Controls
 *
 * Org-wide (tenant-level when multi-tenant lands) AI on/off + per-workflow
 * toggles + governance audit log. Honoured at the request boundary across
 * every AI surface — see ai_governance.py.
 *
 * Required by EU AI Act Art. 14 (human oversight measures) and Art. 72
 * (post-market monitoring system).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  ShieldCheck, AlertTriangle, CheckCircle2, Users, BarChart3, ListChecks,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Toggle({ on, onChange, testId, label, description, disabled = false, tone = 'primary' }) {
  const onClass = tone === 'accent' ? 'bg-[var(--vh-accent)]' : 'bg-[var(--vh-brand-primary)]';
  return (
    <div className={`flex items-start justify-between gap-4 py-4 ${disabled ? 'opacity-60' : ''}`}>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-[var(--vh-text-primary)]">{label}</p>
        {description && <p className="text-xs text-[var(--vh-text-secondary)] mt-1 leading-relaxed">{description}</p>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        disabled={disabled}
        data-testid={testId}
        onClick={() => onChange(!on)}
        className={`shrink-0 w-12 h-7 rounded-full transition-colors flex items-center px-0.5 ${on ? `${onClass} justify-end` : 'bg-[var(--vh-bg-muted)] border border-[var(--vh-border)] justify-start'}`}
      >
        <span className={`w-6 h-6 rounded-full bg-white shadow-sm transition ${on ? '' : 'border border-[var(--vh-border)]'}`} />
      </button>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-2xl p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-[var(--vh-accent)] mb-2">
        <Icon className="w-4 h-4" />
        <span className="text-xs uppercase tracking-[0.16em]">{label}</span>
      </div>
      <p className="font-display text-3xl text-[var(--vh-text-primary)]">{value}</p>
    </div>
  );
}

export default function AdminAIControlsPage() {
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState(null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const [{ data: d }, { data: a }] = await Promise.all([
        axios.get(`${API}/admin/ai-settings`),
        axios.get(`${API}/admin/ai-settings/audit?limit=50`),
      ]);
      setData(d); setAudit(a);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load AI controls.');
    }
  };

  useEffect(() => { load(); }, []);

  const patch = async (payload) => {
    setSaving(true); setError('');
    try {
      const { data: d } = await axios.patch(`${API}/admin/ai-settings`, { ...payload, reason: reason || null });
      setData(d); setSavedAt(new Date()); setReason('');
      // Refresh audit too
      const { data: a } = await axios.get(`${API}/admin/ai-settings/audit?limit=50`);
      setAudit(a);
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  if (!data) {
    return <div className="p-8 text-[var(--vh-text-secondary)]" data-testid="admin-ai-loading">Loading AI controls…</div>;
  }

  const settings = data.settings;
  const globalOn = !!settings.ai_enabled;

  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="admin-ai-controls-page">
      <header className="mb-8">
        <p className="text-xs uppercase tracking-[0.22em] text-[var(--vh-accent)] mb-2">EU AI Act · Art. 14 · Art. 72</p>
        <h1 className="font-display text-3xl md:text-4xl text-[var(--vh-text-primary)] mb-3">AI controls (org-wide)</h1>
        <p className="text-[var(--vh-text-secondary)] leading-relaxed max-w-3xl">
          Disable AI globally, or per workflow, for the whole organisation. Honoured at the request boundary — no LLM is invoked
          when a workflow is off. Every gate decision is logged for post-market monitoring.
        </p>
      </header>

      <section className="grid sm:grid-cols-3 gap-5 mb-8">
        <StatCard icon={ListChecks} label="Gate decisions logged" value={audit?.total_decisions ?? '—'} testId="stat-total-decisions" />
        <StatCard icon={AlertTriangle} label="Blocked by gate" value={audit?.blocked_count ?? '—'} testId="stat-blocked" />
        <StatCard icon={Users} label="Users opted out (data-subject)" value={data?.coverage?.users_total_opted_out ?? '—'} testId="stat-user-optouts" />
      </section>

      <section className="bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-2xl px-6 py-4 mb-6">
        <Toggle
          on={globalOn}
          onChange={(next) => patch({ ai_enabled: next })}
          label="All AI features (org-wide)"
          description="Master switch. When off, no AI feature will run for any user, regardless of their individual preference."
          testId="admin-ai-global-toggle"
          tone="accent"
        />
      </section>

      <section className="bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-2xl px-6 py-2 divide-y divide-[var(--vh-border)] mb-6">
        <header className="py-4">
          <p className="text-xs uppercase tracking-[0.18em] text-[var(--vh-accent)] mb-1">Per-workflow controls</p>
          <p className="text-xs text-[var(--vh-text-secondary)]">Toggle individual workflows. The data-subject toggle in <em>My Preferences</em> can still override these on a per-user basis.</p>
        </header>
        {(data.available_workflows || []).map((wf) => {
          const flagged = !!settings.workflows?.[wf.id];
          const userOpts = data?.coverage?.per_workflow_opted_out?.[wf.id] ?? 0;
          return (
            <div key={wf.id} className="flex items-start justify-between gap-4 py-4">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[var(--vh-text-primary)]">{wf.label}</p>
                <p className="text-xs text-[var(--vh-text-secondary)] mt-1">
                  Audience: <span className="font-mono">{wf.audience}</span> · subject: <span className="font-mono">{wf.subject_kind}</span> · {userOpts} user{userOpts === 1 ? '' : 's'} have opted out individually.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={globalOn && flagged}
                disabled={!globalOn}
                data-testid={`admin-ai-wf-toggle-${wf.id}`}
                onClick={() => patch({ workflows: { [wf.id]: !flagged } })}
                className={`shrink-0 w-12 h-7 rounded-full transition-colors flex items-center px-0.5 ${(globalOn && flagged) ? 'bg-[var(--vh-brand-primary)] justify-end' : 'bg-[var(--vh-bg-muted)] border border-[var(--vh-border)] justify-start'} ${!globalOn ? 'opacity-50' : ''}`}
              >
                <span className={`w-6 h-6 rounded-full bg-white shadow-sm transition ${(globalOn && flagged) ? '' : 'border border-[var(--vh-border)]'}`} />
              </button>
            </div>
          );
        })}
      </section>

      <section className="bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-2xl p-5 mb-8">
        <label htmlFor="admin-ai-reason" className="block text-xs uppercase tracking-[0.16em] text-[var(--vh-accent)] mb-2">Optional reason for the next change</label>
        <input
          id="admin-ai-reason"
          data-testid="admin-ai-reason"
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. customer XYZ requested AI off during pilot"
          className="w-full px-3 py-2 rounded-lg bg-[var(--vh-bg-base)] border border-[var(--vh-border)] text-sm text-[var(--vh-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--vh-accent)]"
        />
        <p className="text-[11px] text-[var(--vh-text-muted)] mt-2">Recorded against the audit row of the next change.</p>
      </section>

      <section className="bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-2xl p-5">
        <header className="flex items-center gap-2 mb-3 text-[var(--vh-brand-primary)]">
          <BarChart3 className="w-4 h-4" />
          <h2 className="font-display text-lg">Gate decision log</h2>
        </header>
        <p className="text-xs text-[var(--vh-text-muted)] mb-4">Last 50 AI-gate decisions. This is the artefact your Art. 72 post-market monitoring system reads.</p>
        {!audit || audit.rows.length === 0 ? (
          <p className="text-sm text-[var(--vh-text-muted)] py-6 text-center">No decisions logged yet.</p>
        ) : (
          <div className="overflow-x-auto" data-testid="ai-gate-log">
            <table className="min-w-full text-xs">
              <thead className="text-[var(--vh-text-muted)] uppercase tracking-[0.12em] text-[10px]">
                <tr>
                  <th className="text-left py-2 pr-3">When</th>
                  <th className="text-left py-2 pr-3">Workflow</th>
                  <th className="text-left py-2 pr-3">Subject</th>
                  <th className="text-left py-2 pr-3">Decision</th>
                  <th className="text-left py-2 pr-3">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--vh-border)]">
                {audit.rows.map((r, i) => (
                  <tr key={`${r.ts}-${i}`} className="text-[var(--vh-text-secondary)]">
                    <td className="py-2 pr-3 font-mono whitespace-nowrap">{(r.ts || '').slice(0, 19).replace('T', ' ')}</td>
                    <td className="py-2 pr-3 font-mono">{r.workflow}</td>
                    <td className="py-2 pr-3 font-mono text-[var(--vh-text-muted)]">{r.subject_user_id ? `${r.subject_user_id.slice(0, 8)}…` : '—'}</td>
                    <td className={`py-2 pr-3 font-semibold ${r.decision === 'blocked' ? 'text-[var(--vh-accent)]' : 'text-[var(--vh-success)]'}`}>{r.decision}</td>
                    <td className="py-2 pr-3 font-mono">{r.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <footer className="mt-6 flex items-center justify-between text-xs text-[var(--vh-text-muted)]">
        <div className="flex items-center gap-2">
          {saving && <span data-testid="admin-ai-saving">Saving…</span>}
          {!saving && savedAt && (
            <span className="inline-flex items-center gap-1 text-[var(--vh-success)]" data-testid="admin-ai-saved">
              <CheckCircle2 className="w-3.5 h-3.5" /> Saved {savedAt.toLocaleTimeString()}
            </span>
          )}
          {error && (
            <span className="inline-flex items-center gap-1 text-[var(--vh-accent)]" data-testid="admin-ai-error">
              <AlertTriangle className="w-3.5 h-3.5" /> {error}
            </span>
          )}
        </div>
        <span className="inline-flex items-center gap-1">
          <ShieldCheck className="w-3.5 h-3.5 text-[var(--vh-brand-primary)]" />
          Every change is audit-logged.
        </span>
      </footer>
    </div>
  );
}
